import {useSelector} from 'react-redux'

import {selectUser} from 'features/User'
import {useAllowedDomainsCheck} from './useAllowedDomainsCheck'
import {DOPE_PRIMARY_DOMAIN} from 'definitions/constant'
import {EnvironmentEnum} from 'interfaces'
import {useEntitlementsQuery} from 'features/Entitlements/hooks'

interface useFeatureFlagReturnType {
  isDopeTenant: boolean
  isPaywalled: boolean
  developmentOnlyFeatureFlag: boolean
  stagingOnlyFeatureFlag: boolean
  nonProductionFeatureFlag: boolean
  dopeInternalFeatureFlag: boolean
  productionDopeFeatureFlag: boolean
  internalAndDopeProductionFeatureFlag: boolean
  isDLPComingSoonScreenEnabled: boolean
  swgDlpBlock: boolean
  swgDlpEarlyAccess: boolean
}

/**
 * @description This hook is used to get the feature flag values
 */
export const useFeatureFlag = (): useFeatureFlagReturnType => {
  const user = useSelector(selectUser)
  const {isPaywalled} = useAllowedDomainsCheck()
  const {swgDlpBlock, swgDlpEarlyAccess} = useEntitlementsQuery({
    enabled: false,
  })

  const primaryDomain = user?.primaryDomain

  /**
   * Default global feature flags for the application.
   */
  const isDopeTenant = primaryDomain === DOPE_PRIMARY_DOMAIN

  const developmentOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.DEVELOPMENT

  const stagingOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.STAGING

  const nonProductionFeatureFlag =
    process.env.REACT_APP_ENV !== EnvironmentEnum.PRODUCTION

  // true if the environment is not production and primaryDomain is dope.security
  const dopeInternalFeatureFlag = nonProductionFeatureFlag && isDopeTenant

  // true if the environment is production and primaryDomain is dope.security
  const productionDopeFeatureFlag = !nonProductionFeatureFlag && isDopeTenant

  // true if the environment is not production or if it's production and primaryDomain is dope.security
  const internalAndDopeProductionFeatureFlag =
    nonProductionFeatureFlag || productionDopeFeatureFlag

  // DLP coming soon screen - enabled for users who do not have early access
  const isDLPComingSoonScreenEnabled = !swgDlpEarlyAccess

  /**
   * Individual feature flags for different features on the platform.
   */

  return {
    nonProductionFeatureFlag,
    isDopeTenant,
    developmentOnlyFeatureFlag,
    stagingOnlyFeatureFlag,
    dopeInternalFeatureFlag,
    productionDopeFeatureFlag,
    internalAndDopeProductionFeatureFlag,
    isPaywalled,
    isDLPComingSoonScreenEnabled,
    swgDlpBlock,
    swgDlpEarlyAccess,
  }
}
